// Web Push Notification Service for PWA

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

interface NotificationSubscription {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

class NotificationService {
  private registration: ServiceWorkerRegistration | null = null;
  private subscription: PushSubscription | null = null;

  // Initialize service worker and request notification permission
  async initialize(): Promise<boolean> {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.warn('Service Worker or Push API not supported');
      return false;
    }

    try {
      // Register service worker
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      this.registration = registration;
      console.log('Service Worker registered:', registration);

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;

      // Request notification permission
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        console.log('Notification permission granted');
        return true;
      } else {
        console.log('Notification permission denied');
        return false;
      }
    } catch (error) {
      console.error('Error initializing service worker:', error);
      return false;
    }
  }

  // Subscribe to push notifications
  async subscribe(): Promise<PushSubscription | null> {
    if (!this.registration) {
      console.error('Service Worker not registered');
      return null;
    }

    try {
      // Check if already subscribed
      let subscription = await this.registration.pushManager.getSubscription();
      
      if (subscription) {
        console.log('Already subscribed to push notifications - ensuring backend registration');
        this.subscription = subscription;
        // Ensure it's registered with backend (might have been lost)
        await this.registerSubscription(subscription);
        return subscription;
      }

      // Create new subscription
      // For web push, we need VAPID public key from backend
      // This will be fetched from backend when needed
      const vapidPublicKey = await this.getVapidPublicKey();
      
      if (!vapidPublicKey) {
        console.error('VAPID public key not available');
        return null;
      }

      // Validate and convert VAPID key
      let applicationServerKey: Uint8Array;
      try {
        applicationServerKey = this.urlBase64ToUint8Array(vapidPublicKey);
        console.log('VAPID key converted successfully, length:', applicationServerKey.length);
        
        // Web Push API requires 65 bytes (uncompressed EC point: 0x04 + 32 bytes X + 32 bytes Y)
        // If key is 64 bytes (without 0x04 prefix), add it
        if (applicationServerKey.length === 64) {
          console.log('Adding 0x04 prefix to VAPID public key');
          const prefixedKey = new Uint8Array(65);
          prefixedKey[0] = 0x04; // Uncompressed point indicator
          prefixedKey.set(applicationServerKey, 1);
          applicationServerKey = prefixedKey;
          console.log('VAPID key after adding prefix, length:', applicationServerKey.length);
        } else if (applicationServerKey.length !== 65) {
          console.error('VAPID public key length is invalid:', applicationServerKey.length, 'Expected 64 or 65 bytes');
          return null;
        }
      } catch (error) {
        console.error('Error converting VAPID public key:', error);
        console.error('VAPID key value:', vapidPublicKey);
        return null;
      }

      subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey
      });

      console.log('Subscribed to push notifications:', subscription);
      this.subscription = subscription;
      
      // Register subscription with backend
      await this.registerSubscription(subscription);
      
      return subscription;
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      return null;
    }
  }

  // Unsubscribe from push notifications
  async unsubscribe(): Promise<boolean> {
    if (!this.subscription) {
      return true;
    }

    try {
      const unsubscribed = await this.subscription.unsubscribe();
      
      if (unsubscribed) {
        // Remove from backend
        await this.unregisterSubscription();
        this.subscription = null;
        console.log('Unsubscribed from push notifications');
      }
      
      return unsubscribed;
    } catch (error) {
      console.error('Error unsubscribing:', error);
      return false;
    }
  }

  // Register subscription with backend
  private async registerSubscription(subscription: PushSubscription): Promise<boolean> {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('User not logged in - cannot register device');
        return false;
      }

      if (!subscription || !subscription.endpoint) {
        console.error('Invalid subscription - missing endpoint');
        return false;
      }

      const subscriptionData: NotificationSubscription = {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: this.arrayBufferToBase64(subscription.getKey('p256dh')!),
          auth: this.arrayBufferToBase64(subscription.getKey('auth')!)
        }
      };

      console.log('Registering device subscription:', {
        endpoint: subscription.endpoint.substring(0, 50) + '...',
        hasKeys: !!(subscriptionData.keys.p256dh && subscriptionData.keys.auth)
      });

      const response = await fetch(`${API_BASE_URL}/devices/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          subscription: subscriptionData,
          platform: 'web'
        })
      });

      const responseData = await response.json().catch(() => ({}));

      if (response.ok) {
        console.log('✅ Device registered successfully:', responseData);
        return true;
      } else {
        console.error('❌ Failed to register device:', {
          status: response.status,
          statusText: response.statusText,
          error: responseData.message || responseData.error || 'Unknown error'
        });
        return false;
      }
    } catch (error: any) {
      console.error('❌ Error registering device subscription:', error);
      console.error('Error details:', error.message, error.stack);
      return false;
    }
  }

  // Unregister subscription from backend
  private async unregisterSubscription(): Promise<boolean> {
    try {
      const token = localStorage.getItem('token');
      if (!token || !this.subscription) {
        return true;
      }

      const response = await fetch(`${API_BASE_URL}/devices/unregister`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          endpoint: this.subscription.endpoint
        })
      });

      return response.ok;
    } catch (error) {
      console.error('Error unregistering subscription:', error);
      return false;
    }
  }

  // Get VAPID public key from backend
  private async getVapidPublicKey(): Promise<string | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/devices/vapid_public_key`);
      if (!response.ok) {
        console.error('Failed to fetch VAPID public key:', response.status, response.statusText);
        return null;
      }
      const data = await response.json();
      const publicKey = data.publicKey || null;
      if (publicKey) {
        console.log('VAPID public key received, length:', publicKey.length, 'characters');
      }
      return publicKey;
    } catch (error) {
      console.error('Error fetching VAPID public key:', error);
      return null;
    }
  }

  // Convert VAPID key from base64 URL to Uint8Array
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  // Convert ArrayBuffer to base64
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });
    return window.btoa(binary);
  }

  // Check if notifications are supported
  isSupported(): boolean {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  }

  // Check if permission is granted
  async hasPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      return false;
    }
    const permission = Notification.permission;
    return permission === 'granted';
  }

  // Get current subscription
  async getSubscription(): Promise<PushSubscription | null> {
    if (!this.registration) {
      return null;
    }
    return await this.registration.pushManager.getSubscription();
  }
}

// Export singleton instance
export default new NotificationService();
